/**
 * Item_Service.ts
 * CRUD + lifecycle operations for Catalog Items  (sk-catalog-api /api/v1/items)
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Items_Url,
  Catalog_Item_Url,
  Catalog_Item_Publish_Url,
  Catalog_Item_Deactivate_Url,
  Catalog_Item_Resolve_Url,
  Catalog_Item_Versions_Url,
  Catalog_Item_Export_Url,
  Catalog_Item_Variants_Url,
} from '../../api/endpoints';
import {
  CatalogItemResponse,
  CatalogItemsResponse,
  CreateItemPayload,
  UpdateItemPayload,
} from '../../schemas/catalog/item';

/* ─────────────────────────────────────────
   List — GET /api/v1/items
───────────────────────────────────────── */

export const getItems = async (params?: Record<string, any>): Promise<CatalogItemsResponse> => {
  const { data } = await axiosInstance.get<CatalogItemsResponse>(Catalog_Items_Url, { params });
  return data;
};

/* ─────────────────────────────────────────
   Get One — GET /api/v1/items/:id
───────────────────────────────────────── */

export const getItem = async (id: string): Promise<CatalogItemResponse> => {
  const { data } = await axiosInstance.get<CatalogItemResponse>(Catalog_Item_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Create — POST /api/v1/items
───────────────────────────────────────── */

export const createItem = async (payload: CreateItemPayload): Promise<CatalogItemResponse> => {
  const { data } = await axiosInstance.post<CatalogItemResponse>(Catalog_Items_Url, payload);
  return data;
};

/* ─────────────────────────────────────────
   Update — PATCH /api/v1/items/:id
───────────────────────────────────────── */

export const updateItem = async (id: string, payload: UpdateItemPayload): Promise<CatalogItemResponse> => {
  const { data } = await axiosInstance.patch<CatalogItemResponse>(Catalog_Item_Url(id), payload);
  return data;
};

/* ─────────────────────────────────────────
   Delete (soft = archive) — DELETE /api/v1/items/:id
   Hard deletes are forbidden by the catalog (system of record); the backend
   transitions the item to ARCHIVED so it leaves the working catalog.
───────────────────────────────────────── */

export const deleteItem = async (id: string): Promise<CatalogItemResponse> => {
  const { data } = await axiosInstance.delete<CatalogItemResponse>(Catalog_Item_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Publish — POST /api/v1/items/:id/publish
───────────────────────────────────────── */

export const publishItem = async (id: string): Promise<CatalogItemResponse> => {
  const { data } = await axiosInstance.post<CatalogItemResponse>(Catalog_Item_Publish_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Publish many — POST /api/v1/items/:id/publish for each id (best-effort).
   Used by the Items screen for "Publish all" and "Publish selected". Runs the
   per-item publish in parallel and reports which ids succeeded / failed so the
   UI can summarise the result.
───────────────────────────────────────── */

export interface BulkPublishFailure {
  id: string;
  /** The API's error code, e.g. `hsn_sac_required` / `illegal_transition`. */
  code: string | null;
  message: string;
}

export const publishItems = async (
  ids: string[],
): Promise<{ ok: string[]; failed: BulkPublishFailure[] }> => {
  const ok: string[] = [];
  const failed: BulkPublishFailure[] = [];
  await Promise.all(
    ids.map(async id => {
      try {
        await publishItem(id);
        ok.push(id);
      } catch (err) {
        // SKCATALOG-CC-002 CR-1/CR-7. This used to swallow the error entirely,
        // so a bulk publish could only ever say "3 failed" — which for the two
        // new refusals is useless: the operator has no way to learn that three
        // taxable services are missing their HSN/SAC codes, or that one of the
        // selected rows was archived. Keeping the code and message lets the
        // caller group the failures by CAUSE and name the fix.
        const data = (err as any)?.response?.data?.error;
        failed.push({
          id,
          code: typeof data?.code === 'string' ? data.code : null,
          message: typeof data?.message === 'string' ? data.message : 'Publish failed.',
        });
      }
    }),
  );
  return { ok, failed };
};

/* ─────────────────────────────────────────
   Deactivate — POST /api/v1/items/:id/deactivate
───────────────────────────────────────── */

export const deactivateItem = async (id: string): Promise<CatalogItemResponse> => {
  const { data } = await axiosInstance.post<CatalogItemResponse>(Catalog_Item_Deactivate_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Resolve — POST /api/v1/items/:id/resolve
   Locale-aware state collapse (for display)
───────────────────────────────────────── */

export const resolveItem = async (id: string, locale?: string): Promise<any> => {
  const { data } = await axiosInstance.get(Catalog_Item_Resolve_Url(id), {
    params: locale ? { locale } : undefined,
  });
  return data;
};

/* ─────────────────────────────────────────
   Versions — GET /api/v1/items/:id/versions
───────────────────────────────────────── */

export const getItemVersions = async (id: string): Promise<any> => {
  const { data } = await axiosInstance.get(Catalog_Item_Versions_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Export — GET /api/v1/items/:id/export
   DPDP data export
───────────────────────────────────────── */

export const exportItem = async (id: string): Promise<any> => {
  const { data } = await axiosInstance.get(Catalog_Item_Export_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Variants — GET /api/v1/items/:id/variants
───────────────────────────────────────── */

export const getItemVariants = async (id: string): Promise<CatalogItemsResponse> => {
  const { data } = await axiosInstance.get<CatalogItemsResponse>(Catalog_Item_Variants_Url(id));
  return data;
};
